import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { API } from "../App";
import { Play, X, ChevronLeft, ChevronRight, Sparkles } from "lucide-react";

// Module-level cache so that re-mounting <VideoShowcase> (or navigating back to
// the landing page) renders the videos *instantly* instead of waiting for the
// API round-trip again. This was the main cause of the "videos loading late"
// complaint — every page paint refetched the list from scratch.
const _cache = { items: null, ts: 0 };
const CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes

export default function VideoShowcase({ section = "", title, eyebrow }) {
  const [items, setItems] = useState(() => (_cache.items && Date.now() - _cache.ts < CACHE_TTL_MS ? _cache.items : null));
  const [active, setActive] = useState(null);
  const rowRef = useRef(null);

  useEffect(() => {
    // Skip the fetch entirely if we already have a warm cache.
    if (_cache.items && Date.now() - _cache.ts < CACHE_TTL_MS) {
      setItems(_cache.items);
      return;
    }
    const setAndCache = (arr) => {
      _cache.items = arr;
      _cache.ts = Date.now();
      setItems(arr);
    };
    const fetchAll = () => axios.get(`${API}/landing-videos`).then(r => setAndCache(r.data.items || [])).catch(() => setAndCache([]));
    if (section) {
      axios
        .get(`${API}/landing-videos?section=${section}`)
        .then(r => {
          if (r.data.items && r.data.items.length > 0) setAndCache(r.data.items);
          else fetchAll();
        })
        .catch(() => fetchAll());
    } else {
      fetchAll();
    }
  }, [section]);

  // While fetching, render a lightweight skeleton so the slot reserves space
  // in the page layout (prevents the "videos pop in late" jank).
  if (items === null) {
    return (
      <section className="max-w-7xl mx-auto px-5 md:px-10 py-20" data-testid={`video-showcase-skeleton`}>
        <div className="max-w-2xl mb-10">
          {eyebrow && <div className="text-xs font-bold tracking-[0.25em] uppercase text-brand">{eyebrow}</div>}
          <h2 className="font-heading text-4xl md:text-6xl font-extrabold mt-3 tracking-tight leading-[1.02]">{title || "Watch PetBond in action"}</h2>
          <p className="text-ink-soft mt-4 text-lg">Stories from the families, vets and NGOs who make this app real.</p>
        </div>
        <div className="rounded-[2rem] bg-gradient-to-br from-surface-muted via-line/40 to-surface-muted animate-pulse" style={{ aspectRatio: "2.4/1" }}/>
        <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {[0,1,2].map(i => (
            <div key={i} className="aspect-video rounded-3xl bg-gradient-to-br from-surface-muted via-line/40 to-surface-muted animate-pulse"/>
          ))}
        </div>
      </section>
    );
  }

  if (items.length === 0) return null;

  // Highlight the "main" video: explicit section_tag="main" wins; otherwise the first item
  const mainIdx = Math.max(0, items.findIndex(v => v.section_tag === "main"));
  const featured = items[mainIdx];
  const rest = items.filter((_, i) => i !== mainIdx);

  const scrollBy = (dir) => {
    if (!rowRef.current) return;
    rowRef.current.scrollBy({ left: dir * Math.min(rowRef.current.clientWidth * 0.8, 600), behavior: "smooth" });
  };

  return (
    <section className="max-w-7xl mx-auto px-5 md:px-10 py-20" data-testid={`video-showcase-${section || "all"}`}>
      <div className="max-w-2xl mb-10">
        {eyebrow && <div className="text-xs font-bold tracking-[0.25em] uppercase text-brand">{eyebrow}</div>}
        <h2 className="font-heading text-4xl md:text-6xl font-extrabold mt-3 tracking-tight leading-[1.02]">{title || "Watch PetBond in action"}</h2>
        <p className="text-ink-soft mt-4 text-lg">Stories from the families, vets and NGOs who make this app real.</p>
      </div>

      {/* HERO — the highlighted main video */}
      <button onClick={() => setActive(featured)} data-testid={`video-hero-${featured.video_id}`} className="group relative w-full text-left rounded-[2rem] overflow-hidden bg-ink shadow-soft hover:shadow-hover transition-all">
        <div className="aspect-[21/9] sm:aspect-[2.4/1] relative">
          <VideoMedia v={featured} className="group-hover:scale-105 transition-transform duration-[1.5s]"/>
          <div className="absolute inset-0 bg-gradient-to-r from-black/85 via-black/40 to-transparent"/>
          <span className="absolute top-5 left-5 text-[10px] font-bold uppercase tracking-[0.2em] bg-brand text-white rounded-full px-3 py-1 inline-flex items-center gap-1"><Sparkles className="w-3 h-3"/>Featured</span>
          <div className="absolute inset-y-0 left-0 right-0 grid place-items-center sm:place-items-start sm:items-center sm:pl-12">
            <div className="text-white max-w-xl px-5 sm:px-0 text-center sm:text-left">
              <h3 className="font-heading text-2xl sm:text-4xl md:text-5xl font-extrabold leading-tight drop-shadow">{featured.title}</h3>
              {featured.description && <p className="opacity-95 mt-3 text-sm sm:text-base line-clamp-2 max-w-md">{featured.description}</p>}
              <div className="mt-5 inline-flex items-center gap-2 bg-white text-ink rounded-full px-5 py-2.5 text-sm font-extrabold group-hover:bg-brand group-hover:text-white transition-colors shadow-soft">
                <Play className="w-4 h-4 ml-0.5"/> Watch now
              </div>
            </div>
          </div>
        </div>
      </button>

      {/* HORIZONTAL CAROUSEL — remaining videos */}
      {rest.length > 0 && (
        <div className="relative mt-8" data-testid="video-row-wrap">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-heading text-lg font-extrabold text-ink-soft">More stories</h3>
            <div className="hidden sm:flex gap-2">
              <button onClick={() => scrollBy(-1)} aria-label="Scroll left" data-testid="video-row-left" className="w-10 h-10 rounded-full bg-surface border border-line grid place-items-center hover:bg-brand hover:text-white hover:border-brand transition-colors"><ChevronLeft className="w-5 h-5"/></button>
              <button onClick={() => scrollBy(1)} aria-label="Scroll right" data-testid="video-row-right" className="w-10 h-10 rounded-full bg-surface border border-line grid place-items-center hover:bg-brand hover:text-white hover:border-brand transition-colors"><ChevronRight className="w-5 h-5"/></button>
            </div>
          </div>
          <div ref={rowRef} className="flex gap-4 overflow-x-auto pb-3 scroll-smooth snap-x snap-mandatory video-row-scroll">
            {rest.map(v => (
              <button key={v.video_id} data-testid={`video-card-${v.video_id}`} onClick={() => setActive(v)} className="group relative shrink-0 w-[78%] sm:w-[44%] lg:w-[31%] snap-start text-left rounded-3xl overflow-hidden bg-ink shadow-soft hover:-translate-y-1 hover:shadow-hover transition-all">
                <div className="aspect-video relative">
                  <VideoMedia v={v}/>
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent"/>
                  <div className="absolute inset-0 grid place-items-center">
                    <div className="w-14 h-14 rounded-full bg-brand text-white grid place-items-center shadow-[0_8px_30px_rgba(255,107,53,0.55)] group-hover:scale-110 transition-transform"><Play className="w-6 h-6 ml-1"/></div>
                  </div>
                  <div className="absolute bottom-0 left-0 right-0 p-5 text-white">
                    <h4 className="font-heading text-lg font-extrabold leading-tight line-clamp-2">{v.title}</h4>
                    {v.description && <p className="text-xs opacity-80 mt-1 line-clamp-1">{v.description}</p>}
                  </div>
                </div>
              </button>
            ))}
          </div>
          <style>{`.video-row-scroll{scrollbar-width:thin;scrollbar-color:#EAE0D5 transparent}.video-row-scroll::-webkit-scrollbar{height:8px}.video-row-scroll::-webkit-scrollbar-thumb{background:#EAE0D5;border-radius:8px}`}</style>
        </div>
      )}

      {active && <VideoPlayerModal video={active} onClose={() => setActive(null)}/>}
    </section>
  );
}

function VideoMedia({ v, className = "" }) {
  // IMPORTANT: never render a <video> element for the thumbnail/preview — it
  // would auto-download the entire base64 / mp4 (often 10-50 MB) on first paint
  // and tank the landing page LCP. We only embed the real <video>/<iframe>
  // *inside* the modal once the user clicks Play.
  if (v.thumbnail) {
    return <img src={v.thumbnail} alt={v.title} loading="lazy" decoding="async" className={`w-full h-full object-cover ${className}`}/>;
  }
  // YouTube / Vimeo — derive a free hosted poster image
  if (v.embed_url || (v.video_url && /youtube\.com|youtu\.be|vimeo\.com/.test(v.video_url))) {
    return <YouTubeFallbackThumb embedUrl={v.embed_url || v.video_url} className={className}/>;
  }
  // Uploaded base64 / direct mp4 without a thumbnail — show a styled gradient
  // placeholder. (Future: extract a poster frame server-side at upload time
  // and store as thumbnail so this branch becomes unused.)
  return (
    <div className={`w-full h-full bg-gradient-to-br from-brand via-brand-blue to-ink grid place-items-center ${className}`}>
      <div className="text-white text-center px-6">
        <div className="w-14 h-14 rounded-full bg-white/15 backdrop-blur grid place-items-center mx-auto mb-2"><Play className="w-6 h-6 ml-0.5"/></div>
        <div className="font-heading text-sm font-bold opacity-90 line-clamp-2">{v.title}</div>
      </div>
    </div>
  );
}

function YouTubeFallbackThumb({ embedUrl, className = "" }) {
  try {
    const m = (embedUrl || "").match(/(?:youtube\.com\/embed\/|youtu\.be\/|v=)([\w-]{6,})/);
    if (m && m[1]) return <img src={`https://img.youtube.com/vi/${m[1]}/hqdefault.jpg`} alt="" loading="lazy" decoding="async" className={`w-full h-full object-cover ${className}`}/>;
  } catch { /* ignore */ }
  return <div className={`w-full h-full bg-gradient-to-br from-brand to-brand-blue ${className}`}/>;
}

function VideoPlayerModal({ video, onClose }) {
  // For uploaded videos the listing endpoint NO LONGER ships `video_base64`
  // (would have been up to 25 MB per video on the public payload). Fetch the
  // playback blob on-demand when the user actually opens the modal.
  const [pb, setPb] = useState(video);
  const [loading, setLoading] = useState(video.source === "upload" && !video.video_base64);

  useEffect(() => {
    if (video.source === "upload" && !video.video_base64) {
      setLoading(true);
      axios
        .get(`${API}/landing-videos/${video.video_id}/playback`)
        .then(r => { setPb({ ...video, ...r.data }); })
        .catch(() => { /* fall through — modal will show error */ })
        .finally(() => setLoading(false));
    }
  }, [video]);

  return (
    <div className="fixed inset-0 z-[60] bg-black/90 grid place-items-center p-4" onClick={onClose} data-testid="video-player-modal">
      <div onClick={e => e.stopPropagation()} className="relative w-full max-w-5xl">
        <button onClick={onClose} className="absolute -top-12 right-0 text-white inline-flex items-center gap-1 text-sm font-bold hover:text-brand"><X className="w-4 h-4"/> Close</button>
        <div className="rounded-2xl overflow-hidden bg-black shadow-2xl">
          {loading ? (
            <div className="w-full aspect-video bg-black grid place-items-center text-white text-sm font-bold">
              <div className="text-center">
                <div className="w-12 h-12 mx-auto rounded-full border-4 border-white/20 border-t-brand animate-spin mb-3"/>
                Loading video…
              </div>
            </div>
          ) : pb.source === "youtube" || pb.source === "vimeo" ? (
            <iframe
              src={`${pb.embed_url}${pb.embed_url?.includes("?") ? "&" : "?"}autoplay=1`}
              title={pb.title}
              className="w-full aspect-video"
              allow="autoplay; encrypted-media; fullscreen"
              allowFullScreen
            />
          ) : pb.source === "upload" && pb.video_base64 ? (
            <video src={pb.video_base64} controls autoPlay className="w-full aspect-video bg-black"/>
          ) : (
            <video src={pb.video_url || pb.embed_url} controls autoPlay className="w-full aspect-video bg-black"/>
          )}
        </div>
        <div className="mt-3 text-white">
          <h3 className="font-heading text-2xl font-extrabold">{pb.title}</h3>
          {pb.description && <p className="text-sm opacity-80 mt-1">{pb.description}</p>}
        </div>
      </div>
    </div>
  );
}
